import { Component, OnDestroy, Input } from '@angular/core';
import {
  ITodo,
  ITag,
  IReview,
  ICandidate,
} from '../../../../../interfaces/graph-types';
import { BehaviorSubject, Subscription } from 'rxjs';
import { WatchAccountService } from '../../../../../services/watch-account.service';
import { CandidateService } from '../../../../../services/candidate.service';
import { ReviewService } from '../../../../../services/review.service';

@Component({
  selector: 'account-todo-review',
  template: require('./account-todo-review.component.pug'),
  styles: [require('./account-todo-review.component.css').toString()],
})
export class AccountTodosReviewComponent implements OnDestroy {
  @Input()
  subject: BehaviorSubject<ITodo>;

  private sub: Subscription;
  private todo: ITodo;
  private steps = [];
  private prosub: Subscription = null;
  private candidateSub: Subscription = null;
  private isLoading: boolean = false;
  private review: IReview;
  private candidateDetails: ICandidate = null;

  // -------------------------------------------------------------------
  public constructor(
    private readonly watchAccount: WatchAccountService,
    private readonly reviewService: ReviewService,
    private readonly candidateService: CandidateService,
  ) {}

  // -------------------------------------------------------------------
  ngOnInit() {
    this.sub = this.subject.subscribe(newValue => {
      this.isLoading = true;
      this.todo = newValue;

      if (!this.todo || this.todo.type != 'review') return; // prevent to catch another type of todo before this component is destroyed

      // Should make a call here for details
      if (this.prosub) this.prosub.unsubscribe();

      this.prosub = this.reviewService
        .getInfo(this.watchAccount.accountId().getValue(), this.todo.id)
        .subscribe(review => {
          this.review = review;

          if (this.candidateSub) this.candidateSub.unsubscribe();

          if (this.todo.candidate) {
            this.candidateSub = this.candidateService
              .getCandidateInfo(
                this.watchAccount.accountId().getValue(),
                this.todo.candidate.id,
              )
              .subscribe(response => {
                this.candidateDetails = response;

                // break this baby apart and allow for markup custom attribute creations []

                this.isLoading = false;
              });
          } else {
            this.isLoading = false;
          }
        });
    });
  }

  // -------------------------------------------------------------------
  public ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
    if (this.prosub) this.prosub.unsubscribe();
    if (this.candidateSub) this.candidateSub.unsubscribe();
  }

  // -------------------------------------------------------------------
  public getChoices(json) {
    return JSON.parse(json).items;
  }

  // -------------------------------------------------------------------
  public getScore(name, choices) {
    let jchoices = JSON.parse(choices);

    const result = jchoices.find(item => item.value === name);

    if (!result) return 0;

    return result.score;
  }

  // -------------------------------------------------------------------
  public getHTML(str) {
    str = str.replaceAll('\n', ' <br/> ');

    let splits = str.split(/[\s,]+/);
    let chunks: string[] = [];

    for (var chunk of splits) {
      if (!chunk) {
        chunk = ' ';
      }
      if (chunk.startsWith('http')) {
        chunks.push(
          '<a href="' + chunk + '" target="_blank">' + chunk + '</a>',
        );
      } else {
        if (chunk.startsWith('www.')) {
          chunks.push(
            '<a href="https://' + chunk + '" target="_blank">' + chunk + '</a>',
          );
        } else {
          chunks.push(chunk);
        }
      }
    }

    return chunks.join(' ');
  }

  // -------------------------------------------------------------------
  public timeDelay(created, updated) {
    let date1 = new Date(created);
    let date2 = new Date(updated);

    let diffTime = Math.abs(+date2 - +date1);

    let sec = Math.ceil(diffTime / 1000);
    if (sec <= 90) return sec.toString() + 'sec.';

    let min = Math.ceil(diffTime / (1000 * 60));
    if (min <= 90) return min.toString() + 'min.';

    let hour = Math.ceil(diffTime / (1000 * 60 * 60));
    if (hour <= 48) return hour.toString() + 'hour.';

    let day = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return day.toString() + 'day.';
  }

  // -------------------------------------------------------------------
  public getExplanation(json) {
    let explanation = JSON.parse(json).explanation;
    if (explanation) return '> ' + explanation;
    return '';
  }

  // -------------------------------------------------------------------
  public findAttribute(text: string) {
    let found = null;

    this.candidateDetails.attributes.forEach((att: any) => {
      let userText = text.toLowerCase();
      let test = ('[[candidate.custom.' + att.name + ']]').toLowerCase();
      if (userText === test) {
        found = att;
        return true;
      }
    });

    return found;
  }

  // -------------------------------------------------------------------
  public findTag(text: string) {
    let found = null;

    this.candidateDetails.tags.forEach((tag: any) => {
      let userText = text.toLowerCase();
      let test = ('[[candidate.tag.' + tag.name + ']]').toLowerCase();
      if (userText === test) {
        found = tag;
        return true;
      }
    });

    return found;
  }

  // -------------------------------------------------------------------
  public changeAttributeDetected(
    data: any,
    newValue: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ): void {
    this.candidateService.setAttributeField(
      this.candidateDetails.id,
      data.id,
      data.attributeValueId,
      'value',
      newValue,
      onsuccess,
      onfailure,
    );
  }

  // -------------------------------------------------------------------
  public tagging(
    tag: ITag,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ): void {
    this.candidateService.setTag(
      this.watchAccount.accountId().getValue(),
      this.todo.candidate.id,
      tag.id,
      !tag.value,
      onsuccess,
      onfailure,
    );
  }
}
